import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import invariant from 'invariant';

import { screenSizes } from '../../theme';

const remToPx = (rem: string) => {
	if (typeof window === 'undefined') {
		return parseFloat(rem) * 16;
	}
	const fontSize = getComputedStyle(document.documentElement).fontSize;
	const rootFontSize = parseFloat(fontSize);
	// Fallback to 16 if parsing fails or fontSize is invalid
	const baseFontSize = isNaN(rootFontSize) ? 16 : rootFontSize;
	return parseFloat(rem) * baseFontSize;
};

interface ContainerWidthContextType {
	containerRef: React.RefObject<HTMLDivElement>;
	isNarrow: boolean;
}

const ContainerWidthContext = createContext<ContainerWidthContextType | undefined>(undefined);

export const ContainerWidthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [isNarrow, setIsNarrow] = useState(false);
	const threshold = useMemo(() => remToPx(screenSizes.tabletXL), []);

	useEffect(() => {
		if (!containerRef.current) return;

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setIsNarrow(entry.contentRect.width < threshold);
			}
		});

		observer.observe(containerRef.current);

		return () => observer.disconnect();
	}, [threshold]);

	const contextProps = useMemo(() => ({ containerRef, isNarrow }), [isNarrow]);
	return <ContainerWidthContext.Provider value={contextProps}>{children}</ContainerWidthContext.Provider>;
};

export const useContainerWidth = (): ContainerWidthContextType => {
	const context = useContext(ContainerWidthContext);

	invariant(context, 'useContainerWidth must be used within a ContainerWidthProvider');

	return context;
};
